import React, { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import PlayerControlsScrubberStartOverLiveLimit from './player-controls-scrubber-start-over-live-limit';
import PlayerControlsScrubberStartOverLiveIndicator from './player-controls-scrubber-start-over-live-indicator';
import PlayerControlsScrubberBase from './player-controls-scrubber-base';
import { PlayerEvent, SeekableRange } from '../../types';
import { usePlayerContext } from '../../_contexts/player-context';
import usePlayerEvent from '../../_hooks/use-player-event';
import useInterval from '@smart-tv/hooks/use-interval';
import useStream from '../../_hooks/use-stream';
import { atom, useAtom, useSetAtom } from 'jotai';
import {
  isSkippingAtom,
  isSkipButtonInProgressAtom,
  validatedTargetPositionAtom
} from '../player-controls-navigation/skip-buttons';
import {
  getLiveProgressPercentage,
  getPosition,
  getProgressPercentageStartOver,
  getProgressSeconds,
  hasProgramRelativePosition
} from './player-controls-scrubber-utils';

const LIVE_PROGRESS_UPDATE_INTERVAL = 1000;

export const hideControlsAtom = atom(false);

interface Props {
  startTime?: Date;
  endTime?: Date;
}

export const PlayerControlsScrubberStartOver = (props: Props) => {
  const { startTime, endTime } = props;
  const [scrubbing, setScrubbing] = useState(false);

  const [progressSeconds, setProgressSeconds] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);

  const [liveProgressPercentage, setLiveProgressPercentage] = useState(0);

  const { player, setIsPlaying } = usePlayerContext();

  const { stream: { duration: durationSeconds = 0 } = {} } = useStream();

  const [isSkipping, setIsSkipping] = useAtom(isSkippingAtom);
  const setValidatedTargetPosition = useSetAtom(validatedTargetPositionAtom);
  const isSkipButtonInProgress = useAtom(isSkipButtonInProgressAtom);

  const [previousProgressValue, setPreviousProgressValue] = useState<number>(-1);

  const [hideControls, setHideControls] = useAtom(hideControlsAtom);

  const [count, setCount] = useState(0);

  const handleUpdateLiveProgress = React.useCallback(() => {
    if (!durationSeconds || !startTime || !endTime) return;

    const seekableRange = player?.getSeekableRange() ?? { start: 0, end: 0 };
    const liveProgressPercentage = getLiveProgressPercentage(durationSeconds, seekableRange.end);

    setLiveProgressPercentage(liveProgressPercentage);
  }, [durationSeconds, endTime, player, startTime]);

  const handleChange = useCallback(
    (value: number) => {
      setScrubbing(true);

      if (value >= liveProgressPercentage) return;

      setProgressPercentage(value);
    },
    [liveProgressPercentage]
  );

  const handleChangeCommitted = useCallback(
    (value: number) => {
      if (!player || !durationSeconds || player?.getSeekableRange()?.end === undefined) return;

      player.setPosition(
        Math.min(getPosition(value, durationSeconds), player.getSeekableRange().end)
      );

      player.play();
      setIsPlaying(true);

      setScrubbing(false);
    },
    [player, durationSeconds, setIsPlaying]
  );

  const timeoutSkipIdRef = useRef<NodeJS.Timeout>();

  const handlePlaytimeUpdated = useCallback(
    (seekableRange: SeekableRange) => {
      if (scrubbing || !durationSeconds) return;

      if (isSkipButtonInProgress[0]) {
        clearTimeout(timeoutSkipIdRef.current);
      }

      if (hasProgramRelativePosition(seekableRange)) {
        const progressSeconds = getProgressSeconds(seekableRange);

        setProgressSeconds(Math.round(progressSeconds));
        setProgressPercentage(getProgressPercentageStartOver(progressSeconds, durationSeconds));
      }

      if (hideControls) {
        if (
          previousProgressValue !== -1 &&
          seekableRange.position - previousProgressValue >= 0.1 &&
          seekableRange.position - previousProgressValue <= 2
        ) {
          // use count to check if stream is playing in flow before we show the controls
          setCount(count + 1);

          if (count > 2) {
            setHideControls(false);
          }
        }
        setPreviousProgressValue(seekableRange.position);

        // Cleanup in case something goes horribly wrong
        setTimeout(() => {
          setHideControls(false);
        }, 4000);
      }

      if (isSkipping) {
        timeoutSkipIdRef.current = setTimeout(() => {
          // If the user is not pushing the skip button, we can reset the skip state
          if (!isSkipButtonInProgress[0]) {
            setIsSkipping(false);
            setValidatedTargetPosition(0);
          }
        }, 250);
      }
    },
    [
      scrubbing,
      durationSeconds,
      isSkipButtonInProgress,
      previousProgressValue,
      hideControls,
      isSkipping,
      setHideControls,
      setIsSkipping,
      setValidatedTargetPosition
    ]
  );

  usePlayerEvent(PlayerEvent.PlayTimeUpdated, handlePlaytimeUpdated);

  useInterval(handleUpdateLiveProgress, LIVE_PROGRESS_UPDATE_INTERVAL);

  const isLivePercentageValid = liveProgressPercentage > 0 && liveProgressPercentage < 100;

  const progress = useMemo(
    () => ({
      duration: durationSeconds,
      progressSeconds,
      progressPercentage,
      liveProgressPercentage,
      startTime,
      endTime
    }),
    [
      durationSeconds,
      progressSeconds,
      progressPercentage,
      liveProgressPercentage,
      startTime,
      endTime
    ]
  );

  return (
    <PlayerControlsScrubberBase
      progress={progress}
      onChange={handleChange}
      onChangeCommitted={handleChangeCommitted}
    >
      {isLivePercentageValid && (
        <Fragment>
          <PlayerControlsScrubberStartOverLiveLimit
            liveProgressPercentage={liveProgressPercentage}
          />
          <PlayerControlsScrubberStartOverLiveIndicator
            liveProgressPercentage={liveProgressPercentage}
          />
        </Fragment>
      )}
    </PlayerControlsScrubberBase>
  );
};

export default memo(PlayerControlsScrubberStartOver);
